/**
 * Headband ribbons: two short verlet chains anchored at the back of the
 * head, with gravity, drag, and the head's motion as wind, drawn as tapering
 * red polylines. Fixed-size buffers; nothing allocates per frame.
 */

import type { Container, Sprite } from "pixi.js";
import type { RenderFeel } from "../feel";
import { type ShapeFactory, setLine } from "../shapes";

const MAX_SEGMENTS = 12;

export class Ribbon {
  private readonly px = new Float64Array(MAX_SEGMENTS + 1);
  private readonly py = new Float64Array(MAX_SEGMENTS + 1);
  private readonly ox = new Float64Array(MAX_SEGMENTS + 1);
  private readonly oy = new Float64Array(MAX_SEGMENTS + 1);
  private readonly segments: Sprite[] = [];
  private initialized = false;
  private prevAnchorX = 0;
  private prevAnchorY = 0;

  /** Width at the anchor, m (tapers toward the tip). */
  widthBase: number;
  /** Segment count in use (at most MAX_SEGMENTS); 0 means the feel's ribbonSegments. */
  segmentCount = 0;

  constructor(
    f: ShapeFactory,
    parent: Container,
    color: number,
    private readonly sideOffset: number,
    widthBase: number,
  ) {
    this.widthBase = widthBase;
    for (let i = 0; i < MAX_SEGMENTS; i++) this.segments.push(f.line(parent, color, widthBase));
  }

  /** Recolor the ribbon (a player's headband color). */
  setTint(color: number): void {
    for (const s of this.segments) s.tint = color;
  }

  /** Reinitialize at the next anchor without replacing the fixed sprite chain. */
  reset(): void {
    this.initialized = false;
  }

  /** Move the coordinate origin without introducing wind or resetting the chain. */
  shiftOrigin(offset: number): void {
    for (let i = 0; i <= MAX_SEGMENTS; i++) {
      this.px[i] = this.px[i]! - offset;
      this.ox[i] = this.ox[i]! - offset;
    }
    this.prevAnchorX -= offset;
  }

  /** Advance the chain with the anchor at (ax, ay); `restDirX/Y` is the direction the ribbon rests toward. */
  update(ax: number, ay: number, restDirX: number, restDirY: number, feel: RenderFeel, dt: number): void {
    const n = Math.min(
      MAX_SEGMENTS,
      Math.max(2, this.segmentCount > 0 ? this.segmentCount : Math.round(feel.ribbonSegments)),
    );
    const len = feel.ribbonSegmentLength;
    if (!this.initialized || Math.abs(ax - this.prevAnchorX) > 2 || Math.abs(ay - this.prevAnchorY) > 2) {
      this.initialized = true;
      for (let i = 0; i <= n; i++) {
        this.px[i] = ax + restDirX * len * i;
        this.py[i] = ay + restDirY * len * i;
        this.ox[i] = this.px[i]!;
        this.oy[i] = this.py[i]!;
      }
      this.prevAnchorX = ax;
      this.prevAnchorY = ay;
    }
    const h = Math.min(dt, 0.033);
    const windX = -(ax - this.prevAnchorX) / Math.max(h, 1e-4);
    const windY = -(ay - this.prevAnchorY) / Math.max(h, 1e-4);
    this.prevAnchorX = ax;
    this.prevAnchorY = ay;
    const damp = Math.max(0, 1 - feel.ribbonDrag * h);
    const accX = restDirX * feel.ribbonStiffness + windX * feel.ribbonWind;
    const accY = restDirY * feel.ribbonStiffness + windY * feel.ribbonWind - feel.ribbonGravity;
    // Verlet integrate the free points.
    for (let i = 1; i <= n; i++) {
      const x = this.px[i]!;
      const y = this.py[i]!;
      const vx = (x - this.ox[i]!) * damp;
      const vy = (y - this.oy[i]!) * damp;
      this.ox[i] = x;
      this.oy[i] = y;
      this.px[i] = x + vx + accX * h * h;
      this.py[i] = y + vy + accY * h * h;
    }
    this.px[0] = ax;
    this.py[0] = ay;
    // Distance constraints, anchor first.
    for (let iter = 0; iter < 3; iter++) {
      for (let i = 1; i <= n; i++) {
        const dx = this.px[i]! - this.px[i - 1]!;
        const dy = this.py[i]! - this.py[i - 1]!;
        const d = Math.sqrt(dx * dx + dy * dy) || 1e-6;
        const corr = (d - len) / d;
        if (i === 1) {
          this.px[i] = this.px[i]! - dx * corr;
          this.py[i] = this.py[i]! - dy * corr;
        } else {
          this.px[i - 1] = this.px[i - 1]! + dx * corr * 0.5;
          this.py[i - 1] = this.py[i - 1]! + dy * corr * 0.5;
          this.px[i] = this.px[i]! - dx * corr * 0.5;
          this.py[i] = this.py[i]! - dy * corr * 0.5;
        }
      }
      this.px[0] = ax;
      this.py[0] = ay;
    }
    for (let i = 0; i < MAX_SEGMENTS; i++) {
      const s = this.segments[i]!;
      if (i >= n) {
        s.visible = false;
        continue;
      }
      s.visible = true;
      const wdt = this.widthBase * (1 - (i / n) * 0.7);
      setLine(
        s,
        this.px[i]!,
        this.py[i]! + this.sideOffset,
        this.px[i + 1]!,
        this.py[i + 1]! + this.sideOffset,
        wdt,
      );
    }
  }
}
